/**
 * Full-Screen Chat Application Entry Point
 *
 * Initializes full-screen chat with ChatGPT-style UI.
 * No Shadow DOM, no toggle button - always visible.
 */

import { WebSocketHandler } from '../shared/websocket.js';
import { StateManager, SESSION_STATES } from '../shared/state.js';
import { ChatUI } from './ui.js';
import { NavBar } from './navbar.js';
import { MobileHandler } from './mobile.js';

const CHAT_VERSION = '1.0.19';

(function() {
  'use strict';

  console.log(`[Chat] v${CHAT_VERSION} loaded`);

  // Module instances
  let wsHandler = null;
  let chatUI = null;
  let stateManager = null;
  let navBar = null;
  let mobileHandler = null;

  /**
   * Load configuration
   */
  function loadConfig() {
    // Extract script's origin (where chat is hosted)
    const scriptOrigin = window.location.origin;
    const scriptHost = window.location.host;

    // WebSocket configuration
    const isSecure = window.location.protocol === 'https:';
    const wsProtocol = isSecure ? 'wss' : 'ws';
    const httpProtocol = isSecure ? 'https' : 'http';

    // Client ID from hostname
    const hostname = window.location.hostname;
    const clientId = hostname.split('.')[0] || 'default';

    const config = {
      clientId,
      wsUrl: `${wsProtocol}://${scriptHost}/ws`,
      apiUrl: `${httpProtocol}://${scriptHost}/api`,
      // Separate images for different uses
      companyLogo: 'https://www.vilniaus-turtas.lt/wp-content/themes/mvo/img/logo.svg',  // For navbar
      botAvatar: '/assets/ugne.png',  // For greeting message
      companyName: '',  // Will be populated from backend
      greeting: '',  // Will be populated from backend
      // Navigation links to vilniaus-turtas.lt website
      navLinks: [
        { label: 'Brokeriai', url: 'https://www.vilniaus-turtas.lt/brokeriai/' },
        { label: 'Testas', url: 'https://www.vilniaus-turtas.lt/testas/' },
        { label: 'NT paieška', url: 'https://www.vilniaus-turtas.lt/nt-paieska/' },
        { label: 'Kontaktai', url: 'https://www.vilniaus-turtas.lt/kontaktai/' }
      ],
      backendConfig: null
    };

    console.log('[Chat] Config loaded:', config);
    return config;
  }

  /**
   * Handle WebSocket messages
   */
  function handleWebSocketMessage(data) {
    console.log('[Chat] WebSocket message:', data);

    switch (data.type) {
      case 'config':
        // Store backend configuration
        if (data.messages && data.placeholders && data.limits) {
          const config = stateManager.getConfig();

          if (!config) {
            console.error('[Chat] Config not initialized in StateManager');
            return;
          }

          config.backendConfig = data;
          stateManager.setConfig(config);

          // Update UI components
          if (chatUI) {
            chatUI.updateBackendConfig(data);
          }
          if (navBar) {
            navBar.updateConfig(data);
          }
          if (wsHandler) {
            wsHandler.updateBackendConfig(data);
          }

          // Update page title
          if (data.company?.name) {
            document.title = `Chat - ${data.company.name}`;
          }

          // Update greeting from backend and show it now
          if (data.messages?.greeting) {
            config.greeting = data.messages.greeting;
            chatUI.config.greeting = data.messages.greeting;
            stateManager.setConfig(config);

            // Show greeting if no messages yet (excluding queue/system messages)
            const messageCount = document.querySelectorAll('.chat-message:not(.chat-message--system)').length;
            if (messageCount === 0) {
              console.log('[Chat] Showing greeting after backend config received');
              chatUI.showGreeting();
            }
          }

          // Update CSS variable if bot avatar is provided in backend config
          if (data.bot_avatar) {
            document.documentElement.style.setProperty('--chat-bot-avatar', `url('${data.bot_avatar}')`);
          }

          console.log('[Chat] Backend config received:', data);
        }
        break;

      case 'message':
        // Bot message
        stateManager.clearMessageTimeout();
        chatUI.showTypingIndicator(false);
        chatUI.renderMessage(data.text, true);
        stateManager.addMessage(data.text, true);
        chatUI.setInputEnabled(true);
        break;

      case 'status_message':
        // Intermediate status message (keep typing indicator)
        chatUI.renderMessage(data.text, true);
        stateManager.addMessage(data.text, true);
        break;

      case 'queued':
        // Entered queue
        chatUI.updateQueuePosition(data);
        stateManager.setQueuePosition(data.position);
        stateManager.setSessionState(SESSION_STATES.IN_QUEUE);
        break;

      case 'queue_update':
        // Queue position updated
        chatUI.updateQueuePosition(data);
        stateManager.setQueuePosition(data.position);
        if (data.position === 0) {
          stateManager.setSessionState(SESSION_STATES.ACTIVE_CHAT);
        }
        break;

      case 'ready':
        // Ready to chat
        chatUI.updateQueuePosition({position: 0});

        // Don't show greeting here - wait for backend config with greeting text
        // (Greeting moved to 'config' message handler)

        chatUI.showConnectionStatus('ready');
        stateManager.setQueuePosition(0);
        if (data.session_id) {
          stateManager.setSessionId(data.session_id);
        }
        stateManager.setSessionState(SESSION_STATES.ACTIVE_CHAT);
        break;

      case 'blocked':
        // Content blocked
        stateManager.clearMessageTimeout();
        chatUI.showTypingIndicator(false);
        stateManager.removeLastUserMessage();
        chatUI.removeLastUserMessage();
        chatUI.renderMessage(data.message || 'Your message was blocked for safety reasons.', true, 'error');
        chatUI.setInputEnabled(true);
        break;

      case 'rate_limited':
        // Rate limited
        stateManager.clearMessageTimeout();
        chatUI.showTypingIndicator(false);
        stateManager.removeLastUserMessage();
        chatUI.removeLastUserMessage();
        chatUI.renderMessage(data.message || 'Please slow down. You are sending messages too quickly.', true, 'error');
        chatUI.setInputEnabled(true);
        break;

      case 'rate_limit_error':
        // Rate limit hit - backend will close connection
        chatUI.renderMessage(data.message || 'Too many connection attempts. Please wait.', true, 'error');
        chatUI.setInputEnabled(false);
        break;

      case 'error':
        // Error message
        stateManager.clearMessageTimeout();
        chatUI.showTypingIndicator(false);
        stateManager.removeLastUserMessage();
        chatUI.removeLastUserMessage();
        chatUI.renderMessage(data.message || 'An error occurred. Please try again.', true, 'error');
        chatUI.setInputEnabled(true);
        break;

      case 'connection_state':
        // WebSocket connection state change
        chatUI.showConnectionStatus(data.state);
        break;

      case 'reconnecting':
        // Reconnection attempt
        chatUI.showConnectionStatus('connecting');
        break;

      case 'offline':
        // Network offline
        chatUI.renderMessage(data.message, true, 'system');
        stateManager.setSessionState(SESSION_STATES.DISCONNECTED);
        break;

      case 'idle_warning':
        // Idle warning
        console.log('[Idle] Warning received');
        chatUI.inputField.placeholder = data.message || 'Are you still there? Type to continue...';
        stateManager.setSessionState(SESSION_STATES.IDLE_WARNING);
        break;

      case 'conversation_ended':
        // Conversation ended due to inactivity
        console.log('[Idle] Session ended');
        stateManager.setSessionState(SESSION_STATES.CONVERSATION_END);
        chatUI.setInputEnabled(false);

        const hasUserMessages = stateManager.messages.some(m => !m.isBot);

        if (hasUserMessages) {
          // Show resume modal or message
          chatUI.renderMessage(
            data.message || 'Session ended due to inactivity. Refresh the page to start a new chat.',
            true,
            'system'
          );
        } else {
          // Simple reconnect
          chatUI.inputField.disabled = false;
          chatUI.inputField.readOnly = true;
          chatUI.inputField.placeholder = 'Session ended. Click to restart...';
          chatUI.inputField.style.cursor = 'pointer';

          const reconnectHandler = () => {
            chatUI.inputField.removeEventListener('focus', reconnectHandler);
            chatUI.inputField.readOnly = false;
            chatUI.inputField.style.cursor = '';

            const sessionId = stateManager.getSessionId();
            wsHandler.connect(sessionId);
          };
          chatUI.inputField.addEventListener('focus', reconnectHandler, { once: true });
        }
        break;

      case 'user_message_echo':
        // Another tab sent a message - display it here
        const messages = document.querySelectorAll('.chat-message--user');
        const lastUserMsg = messages[messages.length - 1];
        const lastText = lastUserMsg?.querySelector('.chat-message__content')?.textContent;

        if (lastText !== data.text) {
          chatUI.renderMessage(data.text, false);
          stateManager.addMessage(data.text, false);
        }

        chatUI.showTypingIndicator(true);
        chatUI.setInputEnabled(false);
        break;

      case 'session_restored':
        // Session successfully restored
        stateManager.setSessionState(SESSION_STATES.RESUME);
        if (data.history && Array.isArray(data.history)) {
          data.history.forEach(msg => {
            chatUI.renderMessage(msg.text, msg.is_bot);
          });
        }
        stateManager.setSessionState(SESSION_STATES.ACTIVE_CHAT);
        break;

      case 'property_cards':
        // Property search results with images
        console.log('[Chat] Property cards received:', data.properties?.length || 0);
        if (data.properties && data.properties.length > 0) {
          chatUI.renderPropertyCards(data.properties, data.total_count);
        }
        break;

      case 'show_inline_form':
        // Show inline contact form in chat bubble
        console.log('[Chat] Showing inline contact form');
        try {
          chatUI.renderInlineForm((formData) => {
            // Handle form submission
            console.log('[Chat] Inline form submitted:', formData);
            wsHandler.send({
              type: 'form_submission',
              data: formData
            });
          });
        } catch (formError) {
          console.error('[Chat] Failed to render inline form:', formError);
          chatUI.renderMessage('Atsiprašome, kontaktų forma nepavyko įkelti. Prašome parašyti savo vardą ir telefoną čia, mes juos matysime. 📝', true, 'error');
        }
        break;

      case 'show_form_button':
        // Show form button (non-intrusive, user-initiated)
        console.log('[Chat] Showing form button');
        chatUI.showFormButton(() => {
          chatUI.showContactForm((formData) => {
            // Handle form submission
            console.log('[Chat] Form submitted:', formData);
            wsHandler.send({
              type: 'form_submission',
              data: formData
            });
          });
        });
        break;

      case 'form_success':
        // Form submitted successfully
        console.log('[Chat] Form submission successful');
        chatUI.hideContactForm();  // Hide modal form (if any)
        chatUI.showInlineFormSuccess(data.message);  // Show success in inline form
        break;

      case 'form_error':
        // Form submission error
        console.log('[Chat] Form submission error:', data.message);
        chatUI.showFormError(data.message);
        break;

      case 'rate_limit_cooldown':
        // Circuit breaker activated - show countdown and retry button
        chatUI.showRateLimitCountdown(data.cooldownSeconds, data.message);
        break;

      default:
        console.warn('[Chat] Unknown message type:', data.type);
    }
  }

  /**
   * Handle user sending a message
   */
  function handleSendMessage(textOrData) {
    // If rate limited and cooldown expired, trigger manual retry
    if (wsHandler.rateLimited && wsHandler.canRetry()) {
      console.log('[Chat] User triggered manual retry');
      wsHandler.manualRetry();
      return;
    }

    // If in IDLE_WARNING state, transition back to ACTIVE_CHAT
    if (stateManager.getSessionState() === SESSION_STATES.IDLE_WARNING) {
      stateManager.setSessionState(SESSION_STATES.ACTIVE_CHAT);
    }

    // Parse input
    let text, metadata;
    if (typeof textOrData === 'string') {
      text = textOrData;
      metadata = { type: 'direct_message' };
    } else {
      text = textOrData.text;
      metadata = textOrData.metadata || { type: 'direct_message' };
    }

    // Add to state
    stateManager.addMessage(text, false);

    // Send via WebSocket
    const sent = wsHandler.send({
      text,
      session_id: stateManager.getSessionId(),
      metadata
    });

    if (!sent) {
      // Failed to send - save as pending
      stateManager.addPendingMessage(text);
      const queuedMsg = chatUI.backendConfig?.messages?.queued || 'Message queued. Will send when connection is restored.';
      chatUI.renderMessage(queuedMsg, true, 'system');
    } else {
      // Disable input while waiting for response
      chatUI.showTypingIndicator(true);
      chatUI.setInputEnabled(false);

      // Set timeout to prevent infinite spinner
      const timeoutId = setTimeout(() => {
        chatUI.showTypingIndicator(false);
        chatUI.setInputEnabled(true);
        const errorMsg = chatUI.backendConfig?.messages?.error || 'An error occurred. Please try again.';
        chatUI.renderMessage(errorMsg, true, 'error');
        console.warn('[Chat] Message timeout after 60s');
      }, 60000);

      stateManager.setMessageTimeout(timeoutId);
    }
  }

  /**
   * Restore previous session
   */
  function restoreSession() {
    const history = stateManager.getConversationHistory();

    if (history && history.length > 0) {
      console.log(`[Chat] Restoring ${history.length} messages`);

      history.forEach(msg => {
        chatUI.renderMessage(msg.text, msg.isBot);
      });
    }

    // Check for pending messages
    const pendingMessages = stateManager.getPendingMessages();
    if (pendingMessages.length > 0) {
      console.log(`[Chat] Resending ${pendingMessages.length} pending messages`);

      pendingMessages.forEach(msg => {
        handleSendMessage(msg.text);
      });
    }
  }

  /**
   * Initialize chat application
   */
  function init() {
    try {
      console.log('[Chat] Initializing...');

      // Load configuration
      const config = loadConfig();

      // Set CSS variable for bot avatar
      if (config.botAvatar) {
        document.documentElement.style.setProperty('--chat-bot-avatar', `url('${config.botAvatar}')`);
      }

      // Get app container
      const appContainer = document.getElementById('app');
      if (!appContainer) {
        throw new Error('App container not found');
      }

      console.log('[Chat] App container found, clearing loading message');

      // Clear loading message
      appContainer.innerHTML = '';

      // Create navbar container
      const navContainer = document.createElement('div');
      navContainer.id = 'nav-container';
      appContainer.appendChild(navContainer);
      console.log('[Chat] Navbar container created');

      // Create hero section
      const heroSection = document.createElement('div');
      heroSection.className = 'chat-hero';
      heroSection.innerHTML = `
        <h1 class="chat-hero__title">Raskite savo svajonių būstą lengviau</h1>
        <p class="chat-hero__subtitle">
          Gaukite nemokamą konsultaciją, rinkos įžvalgas arba suraskite patikimą NT brokerį.
        </p>
      `;
      appContainer.appendChild(heroSection);
      console.log('[Chat] Hero section created');

      // Create chat container
      const chatContainer = document.createElement('div');
      chatContainer.id = 'chat-container';
      appContainer.appendChild(chatContainer);
      console.log('[Chat] Chat container created');

      // Initialize state manager
      stateManager = new StateManager(config.clientId);
      stateManager.init();
      stateManager.setConfig(config);

      // Initialize navbar
      navBar = new NavBar(navContainer, config);
      navBar.render();
      console.log('[Chat] Navbar rendered');

      // Initialize chat UI
      chatUI = new ChatUI(chatContainer, config, stateManager);
      chatUI.init();
      console.log('[Chat] ChatUI initialized');

      // Initialize mobile handler (detects platform, sets up keyboard tracking)
      // CRITICAL: Must pass chatPanel reference (created by chatUI.init())
      mobileHandler = new MobileHandler();
      const chatPanel = document.querySelector('.chat-panel');
      if (chatPanel) {
        mobileHandler.init(chatPanel);
        console.log('[Chat] Mobile handler initialized with chatPanel reference');
      } else {
        console.error('[Chat] CRITICAL: Chat panel not found - mobile keyboard will not work');
      }

      // Set up callbacks
      chatUI.onSendMessage = handleSendMessage;

      // Initialize WebSocket handler
      wsHandler = new WebSocketHandler(config.wsUrl, handleWebSocketMessage, stateManager);

      // Check for existing session with user messages
      const hasSession = stateManager.isSessionValid();
      const hasUserMessages = stateManager.messages.some(m => !m.isBot);

      if (hasSession && hasUserMessages) {
        // Show resume modal - don't auto-connect yet
        console.log('[Chat] Session exists with user messages - showing resume modal');
        chatUI.showResumeModal(
          // onResume callback
          () => {
            console.log('[Resume] User chose to resume session');
            restoreSession();
            const sessionId = stateManager.getSessionId();
            wsHandler.connect(sessionId);
          },
          // onNewChat callback
          () => {
            console.log('[Resume] User chose to start fresh');
            stateManager.resetSession();
            chatUI.clearMessages();
            wsHandler.connect(null);
          }
        );
      } else {
        // No session or no messages - connect normally
        if (stateManager.isSessionValid()) {
          restoreSession();
        }

        const sessionId = stateManager.getSessionId();
        console.log('[Chat] Connecting with session_id:', sessionId ? sessionId.substring(0, 8) + '...' : 'null');
        wsHandler.connect(sessionId);
      }

      console.log('[Chat] Initialization complete');

    } catch (error) {
      console.error('[Chat] Initialization failed:', error);

      // Show error to user
      const appContainer = document.getElementById('app');
      if (appContainer) {
        appContainer.innerHTML = `
          <div style="display: flex; align-items: center; justify-content: center; height: 100%; flex-direction: column; padding: 40px; text-align: center;">
            <h2 style="color: #dc3545; margin-bottom: 20px;">Chat Initialization Failed</h2>
            <p style="color: #666; margin-bottom: 20px;">An error occurred while loading the chat application.</p>
            <button onclick="window.location.reload()" style="padding: 12px 24px; background: #007bff; color: white; border: none; border-radius: 8px; cursor: pointer; font-size: 16px;">
              Reload Page
            </button>
          </div>
        `;
      }
    }
  }

  /**
   * Clean up on page unload
   */
  window.addEventListener('beforeunload', () => {
    if (stateManager) {
      stateManager.saveState();
    }
  });

  // Auto-initialize when DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
